import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import QRCodeModule from 'react-qr-code';
// Handle CJS/ESM interop — the module may resolve as { default: Component } or as Component directly
const QRCode =
  (QRCodeModule as unknown as { default?: typeof QRCodeModule }).default ?? QRCodeModule;
import { Link, useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { AuthShell } from '@/components/auth/auth-shell';
import { MfaSetupHero } from '@/components/auth/mfa-setup-hero';
import { Button } from '@/components/ui/button';
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { getRoleHomeRoute } from '@/lib/role-redirect';
import { confirmMfaSetup, getMe, setActivePropertyId, setupMfa } from '@/services/api.client';

// AUTH-06 — MFA Setup.
// Reached when login returns `mfaSetupRequired: true` (403), or when a user
// voluntarily enables MFA. Server issues a fresh TOTP secret on every call to
// /me/mfa/setup, so we only fetch once per mount.

const schema = z.object({
  code: z
    .string()
    .length(6, 'Enter the 6-digit code from your authenticator app')
    .regex(/^\d{6}$/, 'Code must contain digits only'),
});

type FormValues = z.infer<typeof schema>;

// Decorative tab stepper — always on step 2 "Scan QR" since QR + code entry
// are shown together. Not interactive; purely a visual progress guide.
const STEPS = [{ label: 'Install App' }, { label: 'Scan QR' }, { label: 'Verify Code' }] as const;

function MfaTabStepper() {
  return (
    <div className="flex gap-2">
      {STEPS.map((step, i) => {
        const isActive = i === 1;
        return (
          <div
            key={step.label}
            className="flex flex-1 items-center justify-center rounded-full px-3 py-1.5 text-xs font-semibold"
            style={
              isActive
                ? {
                    backgroundColor: 'var(--cta-primary-bg)',
                    color: 'var(--text-inverse)',
                  }
                : {
                    backgroundColor: 'var(--bg-surface-raised)',
                    color: 'var(--text-tertiary)',
                  }
            }
          >
            {i + 1}&nbsp;&nbsp;{step.label}
          </div>
        );
      })}
    </div>
  );
}

export function MfaSetupPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const setup = useQuery({
    queryKey: ['mfa-setup'],
    queryFn: setupMfa,
    // Only fetch on mount — a refetch would invalidate the previously shown secret.
    staleTime: Infinity,
    gcTime: 0,
    retry: false,
  });

  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { code: '' },
  });

  const confirm = useMutation({
    mutationFn: ({ code }: FormValues) => confirmMfaSetup(code),
    onSuccess: async () => {
      try {
        const me = await getMe();
        const pid = me.propertyRoles?.[0]?.propertyId ?? null;
        if (pid) setActivePropertyId(pid);
        queryClient.setQueryData(['me'], me);
        void navigate(getRoleHomeRoute(me));
      } catch {
        void navigate('/login', { state: { mfaEnabled: true } });
      }
    },
    onError: () => {
      // Clear the input so the user can retry cleanly.
      reset({ code: '' });
    },
  });

  const code = watch('code');

  // Auto-submit once all 6 digits are present.
  useEffect(() => {
    if (code?.length === 6 && /^\d{6}$/.test(code) && !confirm.isPending) {
      void handleSubmit((values) => confirm.mutate(values))();
    }
  }, [code, handleSubmit, confirm]);

  const onSubmit = (values: FormValues) => {
    confirm.mutate(values);
  };

  if (setup.isPending) {
    return (
      <AuthShell
        hero={<MfaSetupHero />}
        title="Set Up Authenticator"
        description="Preparing your authenticator enrolment…"
      >
        <div className="flex items-center justify-center py-8">
          <div className="size-8 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
        </div>
      </AuthShell>
    );
  }

  if (setup.isError || !setup.data) {
    return (
      <AuthShell
        hero={<MfaSetupHero />}
        title="Set Up Authenticator"
        description="We couldn't begin two-factor enrolment. Please try again."
      >
        <div className="flex flex-col items-center gap-4 py-2">
          <Button asChild variant="outline">
            <Link to="/login">Back to sign in</Link>
          </Button>
        </div>
      </AuthShell>
    );
  }

  const { secret, qrUri } = setup.data;
  const isInvalidCode =
    confirm.isError &&
    confirm.error instanceof Object &&
    'status' in confirm.error &&
    (confirm.error as { status: number }).status === 401;

  const apiError =
    confirm.isError &&
    !isInvalidCode &&
    confirm.error instanceof Object &&
    'message' in confirm.error
      ? String((confirm.error as { message: string }).message)
      : null;

  return (
    <AuthShell hero={<MfaSetupHero />} title="Set Up Authenticator">
      <div className="flex flex-col gap-6">
        {/* Visual-only 3-tab stepper — "Scan QR" is the active step */}
        <MfaTabStepper />

        <div className="flex flex-col items-center gap-3">
          <p className="self-start text-sm text-[var(--text-primary)]">
            Scan this QR code with your authenticator app:
          </p>
          <div className="rounded-xl bg-white p-3">
            <QRCode
              value={qrUri}
              size={180}
              aria-label="MFA enrolment QR code"
              viewBox="0 0 256 256"
            />
          </div>

          {/* Manual key pill — terracotta monospace */}
          <div className="w-full">
            <p className="mb-1.5 text-xs text-[var(--text-secondary)]">
              Can&apos;t scan? Enter this key manually:
            </p>
            <div
              className="w-full rounded-lg px-4 py-2 text-center font-mono text-sm tracking-widest select-all"
              style={{
                backgroundColor: 'var(--bg-surface-raised)',
                color: 'var(--text-accent)',
              }}
            >
              {secret}
            </div>
          </div>
        </div>

        <form onSubmit={(e) => void handleSubmit(onSubmit)(e)} noValidate>
          <FieldGroup>
            <Field>
              <FieldLabel>Enter the 6-digit code from your app:</FieldLabel>
              <div className="flex justify-center">
                <Controller
                  name="code"
                  control={control}
                  render={({ field }) => (
                    <InputOTP
                      maxLength={6}
                      value={field.value}
                      onChange={field.onChange}
                      autoFocus
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      disabled={confirm.isPending}
                    >
                      <InputOTPGroup>
                        <InputOTPSlot index={0} />
                        <InputOTPSlot index={1} />
                        <InputOTPSlot index={2} />
                        <InputOTPSlot index={3} />
                        <InputOTPSlot index={4} />
                        <InputOTPSlot index={5} />
                      </InputOTPGroup>
                    </InputOTP>
                  )}
                />
              </div>
              {errors.code && (
                <p className="text-sm text-destructive text-center" role="alert">
                  {errors.code.message}
                </p>
              )}
            </Field>
            {isInvalidCode && (
              <p className="text-sm text-destructive text-center" role="alert">
                Invalid code. Try entering the latest code from your authenticator app.
              </p>
            )}
            {apiError && (
              <p className="text-sm text-destructive text-center" role="alert">
                {apiError}
              </p>
            )}
            <Field>
              <Button type="submit" disabled={confirm.isPending || (code?.length ?? 0) < 6}>
                {confirm.isPending ? 'Confirming…' : 'Confirm & Enable MFA'}
              </Button>
            </Field>
            <p className="text-center text-xs text-[var(--text-secondary)]">
              MFA is required for this role and cannot be skipped.
            </p>
          </FieldGroup>
        </form>
      </div>
    </AuthShell>
  );
}
